<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { currentUser } from '../store.js';
  import { purchasePlan, loadBillingScript, isBillingConfigured } from '../../services/billing-service.js';
  import { isNativeApp } from '../../platform.js';
  import { toast } from '../../ui/dialogs.js';

  const dispatch = createEventDispatcher();

  let selectedPlanId = 'monthly';
  let isPurchasing = false;
  const canPurchase = isBillingConfigured();

  const plans = [
    { id: 'weekend', name: 'Sunday Match Pass 🎟️', price: 49, duration: '1 Day', days: 1, desc: 'Best for casual weekend players' },
    { id: 'monthly', name: 'Monthly Tournament Pass 🏆', price: 249, duration: '30 Days', days: 30, desc: 'Best for league organizers', badge: 'Popular' },
    { id: 'yearly', name: 'Annual Season Pass 🛡️', price: 1199, duration: '365 Days', days: 365, desc: 'Best for clubs and academies' }
  ];

  $: selectedPlan = plans.find(p => p.id === selectedPlanId);

  onMount(() => {
    if (canPurchase) loadBillingScript();
  });

  async function handlePurchase() {
    if (!$currentUser) {
      toast('Please login to upgrade to PRO.', 'error');
      return;
    }

    isPurchasing = true;
    try {
      const result = await purchasePlan(selectedPlan, $currentUser);
      if (result && result.success) {
        currentUser.update(u => ({ ...u, plan: 'pro' }));
        toast('PRO Plan activated successfully! 🎉', 'success');
        dispatch('close');
      }
    } catch (err) {
      toast(err.message || 'Payment failed', 'error');
    } finally {
      isPurchasing = false;
    }
  }

  function handleBackdropClick(event) {
    if (event.target === event.currentTarget) dispatch('close');
  }

  function handleModalKeydown(event) {
    if (event.key === 'Escape') dispatch('close');
  }
</script>

<svelte:window on:keydown={handleModalKeydown} />

<div class="modal-backdrop" role="presentation" on:click={handleBackdropClick}>
  <div class="modal-content" role="dialog" aria-modal="true" aria-label="Upgrade to Pro" tabindex="-1">
    <div class="modal-header">
      <h2>Upgrade to Pro 🚀</h2>
      <button class="close-btn" on:click={() => dispatch('close')}>×</button>
    </div>

    <div class="modal-body" style="text-align:center;">
      <img src="/criccam_pro_logo.png" alt="CricCam Pro Logo" style="width:64px; height:64px; border-radius:12px; margin-bottom:12px; box-shadow:0 0 15px rgba(239,68,68,0.5);" />
      <h3 style="margin: 0 0 8px 0; color: #fff;">Unlock Broadcasting Tools</h3>
      <p style="color: #cbd5e1; font-size: 13px; margin-bottom: 16px; line-height: 1.4;">
        Get the ultimate television-style overlay experience for your local matches.
      </p>

      <ul style="text-align: left; background: var(--surface-2, #1e293b); padding: 12px 12px 12px 28px; border-radius: 8px; margin-bottom: 20px; font-size:12px; color: #e2e8f0; line-height:1.6;">
        <li>🎥 TV-style Video Overlays & Animations</li>
        <li>📱 Dual-Phone Syncing (Broadcast/Recorder)</li>
        <li>💼 Sponsor Logo Integration (Overlays & Posters)</li>
        <li>🖼️ Watermark-Free Video & Posters</li>
        <li>☁️ Unlimited Cloud Matches Sync</li>
      </ul>

      <div style="text-align: left; font-size: 11px; text-transform: uppercase; font-weight: 800; color: #94a3b8; margin-bottom: 8px; letter-spacing: 0.5px;">Plans</div>

      <div style="display:flex; flex-direction:column; gap:8px; margin-bottom:20px;">
        {#each plans as p, i_each (i_each)}
          <div
            class="plan-card {selectedPlanId === p.id ? 'active' : ''}"
            on:click={() => selectedPlanId = p.id}
            role="button"
            tabindex="0"
            on:keydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectedPlanId = p.id; } }}
            style="position:relative; display:flex; justify-content:space-between; align-items:center; padding:10px 14px; background:var(--surface-2, #1e293b); border: 2px solid {selectedPlanId === p.id ? '#fbbf24' : '#334155'}; border-radius:8px; cursor:pointer; transition:all 0.15s;"
          >
            {#if p.badge}
              <span style="position:absolute; top:-8px; right:10px; background:#fbbf24; color:#1e293b; font-size:8px; font-weight:900; padding:1px 5px; border-radius:10px; text-transform:uppercase;">{p.badge}</span>
            {/if}
            <div style="text-align:left; padding-right: 12px;">
              <div style="font-weight:800; color:#fff; font-size:13px;">{p.name}</div>
              <div style="font-size:10px; color:#94a3b8; margin-top:2px;">{p.desc}</div>
            </div>
            <div style="text-align:right; flex-shrink: 0;">
              <div style="font-size:16px; font-weight:900; color:#fbbf24;">₹{p.price}</div>
              <div style="font-size:9px; color:#cbd5e1; margin-top:1px;">{p.duration}</div>
            </div>
          </div>
        {/each}
      </div>

      {#if canPurchase}
        <button class="buy-btn" on:click={handlePurchase} disabled={isPurchasing}>
          {isPurchasing ? 'Processing...' : `Pay Securely via Razorpay (₹${selectedPlan.price})`}
        </button>
        <p style="font-size:9px; color:#64748b; margin-top:8px;">
          Payments are processed securely by Razorpay.
        </p>
      {:else}
        <div class="manual-activate">
          ⏳ {isNativeApp
            ? 'In-app purchase jald aa raha hai. Abhi PRO chahiye to FFPL admin se contact karo'
            : 'Online payment jald aa raha hai. Abhi PRO chahiye to FFPL admin se contact karo'}
          {#if $currentUser?.email}&nbsp;— apna account email batao: <b>{$currentUser.email}</b>{/if}
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
    background: rgba(0, 0, 0, 0.85); backdrop-filter: blur(4px);
    display: flex; justify-content: center; align-items: center; z-index: 10000;
  }
  .modal-content {
    background: var(--surface, #0f172a); border: 1px solid var(--border, #334155); border-radius: 12px;
    width: 90%; max-width: 400px; max-height: 90vh; overflow-y: auto;
    box-shadow: 0 10px 25px rgba(0,0,0,0.5);
  }
  .modal-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 14px 18px; border-bottom: 1px solid var(--border-soft, #1e293b);
  }
  .modal-header h2 { margin: 0; font-size: 16px; color: #f8fafc; }
  .close-btn {
    background: none; border: none; color: #94a3b8; font-size: 24px;
    cursor: pointer; padding: 8px; line-height: 1;
  }
  .modal-body { padding: 20px 18px; }
  .plan-card:hover {
    background: #273549 !important;
  }
  .buy-btn {
    width: 100%;
    padding: 14px;
    border-radius: 10px;
    border: none;
    background: #fbbf24;
    color: #0f172a;
    font-weight: 800;
    font-size: 14px;
    cursor: pointer;
    text-transform: uppercase;
    transition: all 0.2s;
  }
  .buy-btn:hover:not(:disabled) {
    background: #f59e0b;
    transform: translateY(-1px);
  }
  .buy-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  .manual-activate {
    background: rgba(251, 191, 36, 0.08);
    border: 1px solid rgba(251, 191, 36, 0.35);
    color: #fde68a;
    border-radius: 10px;
    padding: 12px;
    font-size: 12px;
    line-height: 1.55;
    text-align: left;
  }
</style>
